import { useMemo } from 'react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/adminClient';

export function usePermissions() {
  const { adminUser } = useAdminAuth();
  const roles = adminUser?.roles || [];

  const { data: rolePermissions = [], isLoading } = useQuery({
    queryKey: ['role-permissions', roles.join(',')],
    queryFn: async () => {
      if (!roles.length) return [];
      const { data } = await supabase
        .from('role_permissions')
        .select('permission_key')
        .in('role', roles);
      return (data || []).map(r => r.permission_key);
    },
    enabled: roles.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const permissionSet = useMemo(() => new Set(rolePermissions), [rolePermissions]);

  // admin and super_admin roles bypass granular permission checks (god-mode)
  const isSuperRole = useMemo(
    () => roles.some(r => r === 'admin' || r === 'super_admin'),
    [roles]
  );

  // During loading, never deny — wait for resolution
  const hasPermission = (key: string) => isLoading || isSuperRole || permissionSet.has(key);
  const hasAnyPermission = (...keys: string[]) => isLoading || isSuperRole || keys.some(k => permissionSet.has(k));

  return { hasPermission, hasAnyPermission, permissions: rolePermissions, isSuperRole, isLoading };
}
